import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api';
import type { Feed } from '../types';
import { decodeHtmlEntities, stripHtml } from '../utils/html';
import { StatusMessage } from '../components/StatusMessage';
import { FeedForm } from '../components/FeedForm';
import { FeedCard } from '../components/FeedCard';
import { useChannels } from '../providers/ChannelProvider';
import { useUser } from '../providers/UserProvider';
import type { CreateFeedRequest, UpdateFeedRequest, FeedElement } from '../repositories/feedRepository';
import * as feedRepository from '../repositories/feedRepository';
import { formatAge } from '../utils/formatTime';
import { safeHref } from '../utils/html';

export default function FeedsPage() {
  // Read through the context, so the admin-mode toggle re-renders this page
  // by subscription rather than by luck (issue #889).
  const { isAdmin: admin } = useUser();
  const { refreshChannels } = useChannels();
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [elements, setElements] = useState<FeedElement[]>([]);
  const [selectedFeedId, setSelectedFeedId] = useState<string>('');
  const [loadingFeeds, setLoadingFeeds] = useState(true);
  const [loadingElements, setLoadingElements] = useState(true);
  const [status, setStatus] = useState({ message: '', isError: false });

  const loadFeeds = async () => {
    setLoadingFeeds(true);
    try {
      setFeeds(await feedRepository.listFeeds());
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Failed to load feeds', isError: true });
    } finally {
      setLoadingFeeds(false);
    }
  };

  const loadElements = async (feedId?: string) => {
    setLoadingElements(true);
    try {
      setElements(await feedRepository.listFeedElements(feedId || undefined));
    } catch (err) {
      if (feedId && err instanceof ApiError && err.status === 404) {
        setSelectedFeedId('');
        setStatus({ message: 'Selected feed no longer exists. Showing all feeds instead.', isError: false });
        try {
          setElements(await feedRepository.listFeedElements(undefined));
          return;
        } catch (retryErr) {
          setStatus({ message: (retryErr as Error).message || 'Failed to load feed items', isError: true });
          return;
        }
      }
      setStatus({ message: (err as Error).message || 'Failed to load feed items', isError: true });
    } finally {
      setLoadingElements(false);
    }
  };

  useEffect(() => { loadFeeds(); }, []);
  useEffect(() => { loadElements(selectedFeedId); }, [selectedFeedId]);

  const handleCreate = async (data: CreateFeedRequest) => {
    try {
      await feedRepository.createFeed(data);
      setStatus({ message: 'Feed added', isError: false });
      await refreshChannels();
      await loadFeeds();
      await loadElements(selectedFeedId);
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Failed to add feed', isError: true });
    }
  };

  const handleSave = async (feedId: string, data: UpdateFeedRequest) => {
    try {
      await feedRepository.updateFeed(feedId, data);
      setStatus({ message: 'Feed updated', isError: false });
      await loadFeeds();
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Failed to update feed', isError: true });
    }
  };

  const handleDelete = async (feedId: string) => {
    try {
      await feedRepository.deleteFeed(feedId);
      setStatus({ message: 'Feed deleted', isError: false });
      await refreshChannels();
      await loadFeeds();
      if (selectedFeedId === feedId) setSelectedFeedId('');
      else await loadElements(selectedFeedId);
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Failed to delete feed', isError: true });
    }
  };

  const handleRefresh = async () => {
    setStatus({ message: 'Refreshing feeds…', isError: false });
    try {
      const result = await feedRepository.refreshMyFeeds();
      const inserted = result.results.reduce((n, r) => n + r.inserted, 0);
      setStatus({ message: `Refreshed — ${inserted} new item${inserted === 1 ? '' : 's'}`, isError: false });
      await loadElements(selectedFeedId);
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Failed to refresh feeds', isError: true });
    }
  };

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <div>
          <div className="pane-eyebrow">Settings</div>
          <h1>Manage Subscriptions</h1>
        </div>
        <div className="admin-header-actions">
          <button className="ghost-button" onClick={handleRefresh}>Refresh</button>
          <Link to="/tldr" className="ghost-button nav-link-button">Back to reader</Link>
        </div>
      </header>
      <StatusMessage message={status.message} isError={status.isError} />

      <section className="admin-grid" style={{ marginBottom: '1.5rem' }}>
        <FeedForm onSubmit={handleCreate} />
        <section className="admin-panel">
          <div className="admin-panel-header">
            <h2>Your RSS feeds</h2>
            <span>{loadingFeeds ? 'Loading…' : `${feeds.length} feed${feeds.length === 1 ? '' : 's'}`}</span>
          </div>
          <div className="site-list">
            {loadingFeeds ? (
              <div className="empty-panel">Loading feeds…</div>
            ) : feeds.length === 0 ? (
              <div className="empty-panel">No feeds yet. Add one above.</div>
            ) : (
              feeds.map((feed) => (
                <FeedCard
                  key={feed.id}
                  feed={feed}
                  isAdmin={admin}
                  onSave={handleSave}
                  onDelete={handleDelete}
                />
              ))
            )}
          </div>
        </section>
      </section>

      <section className="admin-panel">
        <div className="admin-panel-header">
          <h2>Feed items</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <select
              className="text-input"
              value={selectedFeedId}
              onChange={(e) => setSelectedFeedId(e.target.value)}
              style={{ minWidth: '180px' }}
            >
              <option value="">All feeds</option>
              {feeds.map((f) => (
                <option key={f.id} value={f.id}>{f.title || f.feed_url}</option>
              ))}
            </select>
            <span className="muted-count">
              {loadingElements ? 'Loading…' : `${elements.length} items`}
            </span>
          </div>
        </div>
        {loadingElements ? (
          <div className="empty-panel">Loading…</div>
        ) : elements.length === 0 ? (
          <div className="empty-panel">
            {feeds.length === 0
              ? 'No feeds yet — add one to get started.'
              : 'No items yet. Try refreshing your feeds.'}
          </div>
        ) : (
          <div className="feed-elements-list">
            {elements.map((el) => (
              <FeedElementRow key={el.id} el={el} showFeedName={!selectedFeedId} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function FeedElementRow({ el, showFeedName }: { el: FeedElement; showFeedName: boolean }) {
  const title = decodeHtmlEntities(el.title) || '(untitled)';
  const summary = stripHtml(el.summary || '');
  const age = formatAge(el.published_at || el.created_at, { compact: true });

  return (
    <div className="feed-element-row">
      <div className="feed-element-main">
        <div className="feed-element-title-line">
          {el.url ? (
            <a href={safeHref(el.url)} target="_blank" rel="noopener noreferrer" className="feed-element-title">
              {title}
            </a>
          ) : (
            <span className="feed-element-title">{title}</span>
          )}
          {el.canonical_domain && (
            <span className="feed-element-domain">{el.canonical_domain}</span>
          )}
          {age && <span className="feed-element-age">{age}</span>}
        </div>
        {summary && (
          <div className="feed-element-summary">{summary.length > 200 ? summary.slice(0, 200) + '…' : summary}</div>
        )}
        <div className="feed-element-meta">
          {showFeedName && el.feed_name && (
            <span className="feed-element-feed-name">{el.feed_name}</span>
          )}
          {el.tier && <span className="feed-element-tier">Tier {el.tier}</span>}
        </div>
      </div>
    </div>
  );
}
